import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    MapPinIcon,
    ClockIcon,
    StarIcon,
    ChevronDownIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { TieredPackage } from '../../services/tieredPackageService';
import { usePricing } from '../../contexts/PricingContext';
import { pricingService } from '../../services/pricingService';
import { currencyService } from '../../services/currencyService';
import { PricingTier, FinalPrice } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';

interface TieredPackageCardProps {
    package: TieredPackage;
}

const TieredPackageCard: React.FC<TieredPackageCardProps> = ({ package: pkg }) => {
    const { actualTheme } = useTheme();
    const pricingContext = usePricing();
    const { tier: contextTier, nationalityType, displayCurrency, countryCode, pax } = pricingContext;
    const [selectedTier, setSelectedTier] = useState<PricingTier>(contextTier);
    const [calculatedPrice, setCalculatedPrice] = useState<FinalPrice | null>(null);
    const [originalPrice, setOriginalPrice] = useState<FinalPrice | null>(null);
    const [isLoadingPrice, setIsLoadingPrice] = useState(false);
    const [showTierDropdown, setShowTierDropdown] = useState(false);
    const hasDiscount = !!pkg.discountedTierVariants?.[selectedTier];

    // Calculate price when tier or pricing context changes
    useEffect(() => {
        const calculatePrice = async () => {
            setIsLoadingPrice(true);
            try {
                const contextWithTier = { ...pricingContext, tier: selectedTier };
                const result = await pricingService.calculatePackagePrice(pkg, contextWithTier);
                setCalculatedPrice(result);

                // Calculate original undiscounted price if discount exists
                if (pkg.discountedTierVariants?.[selectedTier]) {
                    // Pass useOriginalPricing=true so both client-side and backend skip discountedTierVariants
                    const originalContext = { ...contextWithTier, useOriginalPricing: true };
                    const originalResult = await pricingService.calculatePackagePrice(pkg, originalContext);
                    setOriginalPrice(originalResult);
                } else {
                    setOriginalPrice(null);
                }
            } catch (error) {
                console.error('Error calculating package price:', error);
            } finally {
                setIsLoadingPrice(false);
            }
        };

        calculatePrice();
    }, [pkg, selectedTier, nationalityType, displayCurrency, countryCode, pax]);

    const currentTierData = pkg?.tierVariants?.[selectedTier];

    const getTierColor = (tier: PricingTier) => {
        switch (tier) {
            case PricingTier.LUXURY:
                return 'bg-gradient-to-r from-amber-500 to-yellow-600';
            case PricingTier.STANDARD:
                return 'bg-gradient-to-r from-blue-500 to-indigo-600';
            case PricingTier.BUDGET:
                return 'bg-gradient-to-r from-green-500 to-emerald-600';
        }
    };

    const getTierLabel = (tier: PricingTier) => {
        switch (tier) {
            case PricingTier.LUXURY:
                return 'Luxury';
            case PricingTier.STANDARD:
                return 'Standard';
            case PricingTier.BUDGET:
                return 'Budget';
        }
    };

    return (
        <div
            className={`relative rounded-2xl overflow-hidden transition-all duration-300 group ${actualTheme === 'dark'
                ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/30'
                : 'bg-white border border-neutral-200/50'
                } floating-card hover:floating-card-hover shadow-modern hover:shadow-modern-lg`}
        >
            <Link
                to={`/tiered-packages/${pkg.id}`}
                className="absolute inset-0 z-10"
                aria-label={`View details for ${pkg.name}`}
            />

            {/* Gallery Image */}
            <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
                {pkg.gallery && pkg.gallery.length > 0 ? (
                    <img
                        src={pkg.gallery[0]}
                        alt={pkg.name}
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                        <MapPinIcon className="w-16 h-16 text-white opacity-50" />
                    </div>
                )}

                {/* Tier Badge */}
                <div className="absolute top-4 left-4 z-20">
                    <div className={`${getTierColor(selectedTier)} text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2`}>
                        <StarSolidIcon className="h-4 w-4" />
                        {getTierLabel(selectedTier)}
                    </div>
                </div>

                {/* Tier Switcher Dropdown */}
                <div className="absolute top-4 right-4 z-20">
                    <div className="relative">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowTierDropdown(!showTierDropdown);
                            }}
                            className={`${actualTheme === 'dark' ? 'bg-slate-800/95' : 'bg-white/95'
                                } backdrop-blur-md px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2 hover:scale-105 transition-transform`}
                        >
                            Switch Tier
                            <ChevronDownIcon className="h-4 w-4" />
                        </button>

                        {showTierDropdown && (
                            <div
                                className={`absolute top-full right-0 mt-2 ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
                                    } rounded-xl shadow-2xl overflow-hidden min-w-[160px] border ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
                                    }`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {[PricingTier.LUXURY, PricingTier.STANDARD, PricingTier.BUDGET].map((tier) => (
                                    <button
                                        key={tier}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (pkg?.tierVariants?.[tier]) {
                                                setSelectedTier(tier);
                                            }
                                            setShowTierDropdown(false);
                                        }}
                                        disabled={!pkg?.tierVariants?.[tier]}
                                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors flex items-center justify-between ${!pkg?.tierVariants?.[tier]
                                            ? 'opacity-50 cursor-not-allowed text-gray-400'
                                            : selectedTier === tier
                                                ? actualTheme === 'dark'
                                                    ? 'bg-slate-700 text-white'
                                                    : 'bg-primary-50 text-primary-800'
                                                : actualTheme === 'dark'
                                                    ? 'text-slate-300 hover:bg-slate-700'
                                                    : 'text-neutral-700 hover:bg-neutral-50'
                                            }`}
                                    >
                                        <span>{getTierLabel(tier)}</span>
                                        {selectedTier === tier && <CheckCircleIcon className="h-4 w-4" />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            </div>

            {/* Content */}
            <div className="p-4 sm:p-5 md:p-6">
                <div className="mb-3">
                    <h3 className={`text-lg sm:text-xl font-bold mb-2 line-clamp-2 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'}`}>
                        {pkg.name}
                    </h3>
                    <p className={`text-xs sm:text-sm line-clamp-2 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                        {pkg.overview}
                    </p>
                </div>

                {/* Quick Info */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className={`flex items-center gap-2 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                        <ClockIcon className="h-4 w-4" />
                        <span className="text-sm">{pkg.durationDays} Days</span>
                    </div>
                    <div className={`flex items-center gap-2 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                        <MapPinIcon className="h-4 w-4" />
                        <span className="text-sm truncate">{pkg.destinations[0]}</span>
                    </div>
                </div>

                {/* Tier-Specific Highlights */}
                <div className={`mb-4 p-3 rounded-lg ${actualTheme === 'dark' ? 'bg-slate-700/50' : 'bg-neutral-50'}`}>
                    <h4 className={`text-xs font-semibold mb-2 uppercase ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'}`}>
                        {getTierLabel(selectedTier)} Includes:
                    </h4>
                    <ul className="space-y-1">
                        {(currentTierData?.inclusions || []).slice(0, 3).map((item: string, idx: number) => (
                            <li key={idx} className={`text-xs flex items-start gap-2 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                                <CheckCircleIcon className="h-3 w-3 mt-0.5 flex-shrink-0 text-green-500" />
                                <span className="line-clamp-1">{item}</span>
                            </li>
                        ))}
                    </ul>
                    {(currentTierData?.inclusions?.length ?? 0) > 3 && (
                        <p className="text-xs text-primary-600 dark:text-primary-400 mt-2">
                            +{(currentTierData?.inclusions?.length ?? 0) - 3} more
                        </p>
                    )}
                </div>

                {/* Pricing */}
                <div className="flex items-center justify-between relative z-20">
                    <div>
                        {/* WAS / NOW discount display */}
                        {hasDiscount && originalPrice && calculatedPrice && !isLoadingPrice && (
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">WAS:</span>
                                <span className="text-sm line-through font-medium text-neutral-400">
                                    {currencyService.formatCurrency(originalPrice.total, originalPrice.currency)}
                                </span>
                                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                    -{Math.round((1 - calculatedPrice.total / originalPrice.total) * 100)}%
                                </span>
                            </div>
                        )}
                        <div className="flex items-baseline gap-1">
                            {hasDiscount && !isLoadingPrice && (
                                <span className="text-[10px] font-bold uppercase tracking-wider text-green-600">NOW:</span>
                            )}
                            <div className={`text-2xl font-bold ${hasDiscount ? 'text-green-600' : (actualTheme === 'dark' ? 'text-white' : 'text-primary-800')}`}>
                                {isLoadingPrice ? (
                                    <span className="animate-pulse opacity-50">...</span>
                                ) : calculatedPrice ? (
                                    currencyService.formatCurrency(calculatedPrice.total, calculatedPrice.currency)
                                ) : (
                                    'N/A'
                                )}
                            </div>
                        </div>
                        <div className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            {pricingContext.nationalityType === 'LOCAL' ? 'Local Rate' : 'International Rate'}
                        </div>
                    </div>

                    <Link
                        to={`/tiered-packages/${pkg.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${actualTheme === 'dark'
                            ? 'bg-primary-700 hover:bg-primary-600 text-white'
                            : 'bg-primary-800 hover:bg-primary-700 text-white'
                            } shadow-lg hover:shadow-xl`}
                    >
                        View Details
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default TieredPackageCard;
